import React from 'react';
import { createPortal } from 'react-dom';
import { BookOpenIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { t } from '../../utils/translations';
import Button from '../ui/Button';

interface SessionClosedPromptProps {
  open: boolean;
  caseName: string;
  sessionNumber: number | null;
  onNow: () => void;
  onLater: () => void;
}

/**
 * Offered the moment the report is filed, because that is when the meeting is still in the
 * counselor's head — an hour later the detail worth writing down is already the summary.
 *
 * It asks rather than opens: the report is an obligation, this is not, and a step that forces
 * itself on a counselor who has ten minutes for it would be the fastest way to make it resented.
 */
const SessionClosedPrompt: React.FC<SessionClosedPromptProps> = ({
  open,
  caseName,
  sessionNumber,
  onNow,
  onLater,
}) => {
  if (!open || typeof document === 'undefined') return null;

  const meta = [
    sessionNumber !== null
      ? `${t.sessionReports.sessionNumber} ${sessionNumber}`
      : null,
    caseName,
  ]
    .filter(Boolean)
    .join(' · ');

  return createPortal(
    <div
      className="fixed inset-0 z-[1500] flex items-stretch justify-center sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-closed-title"
    >
      <div className="absolute inset-0 bg-slate-900/40" aria-hidden="true" onClick={onLater} />

      {/* Same box, header/body/footer chrome as the rest of the report flow (type chooser,
          wizard) — the report was just filed through that flow, so this shouldn't read as a
          smaller, different screen. Content stays top-anchored and the actions live in their
          own footer, so the box reads like a short list rather than floating dead center. */}
      <div className="safe-pt safe-pb relative flex h-full w-full flex-col overflow-hidden bg-white shadow-lg sm:h-[min(90vh,38rem)] sm:max-w-lg sm:rounded-xl sm:border sm:border-slate-200">
        <div className="flex shrink-0 items-center gap-3 border-b border-slate-100 px-4 py-3.5 sm:px-5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-700">
            <CheckIcon className="h-[18px] w-[18px]" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 id="session-closed-title" className="text-[15px] font-semibold text-slate-900">
              {t.sessionClosed.title}
            </h2>
            {meta && <p className="truncate text-[13px] text-slate-500">{meta}</p>}
          </div>
          <button
            type="button"
            onClick={onLater}
            aria-label={t.common.close}
            className="shrink-0 rounded-md p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-5">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-brand-50 text-brand-600">
              <BookOpenIcon className="h-5 w-5" aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-900">{t.sessionClosed.prompt}</p>
              <p className="mt-1 text-[13px] leading-[19px] text-slate-600">
                {t.sessionClosed.body}
              </p>
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-between gap-2 border-t border-slate-100 px-4 py-3 sm:px-5">
          <Button onClick={onLater}>{t.sessionClosed.later}</Button>
          <Button variant="primary" onClick={onNow}>
            {t.sessionClosed.now}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SessionClosedPrompt;
